import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeftRight, ArrowLeft, Send, Plus, Search, Trash2, X, Loader2,
  MessagesSquare, ChevronRight, AlertCircle, Megaphone, UserSearch, Crown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { marketBoardApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const KIND_META = {
  looking_for_club: {
    label: { it: 'Cerco Club', en: 'Looking for Club' },
    Icon: UserSearch,
    color: 'text-[#00F0FF] border-[#00F0FF]/40 bg-[#00F0FF]/10',
  },
  looking_for_player: {
    label: { it: 'Cerco Giocatore', en: 'Looking for Player' },
    Icon: Megaphone,
    color: 'text-[#FFEA00] border-[#FFEA00]/40 bg-[#FFEA00]/10',
  },
};

const timeAgo = (iso, isItalian) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return isItalian ? 'adesso' : 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}${isItalian ? 'm fa' : 'm ago'}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${isItalian ? ' fa' : ' ago'}`;
  const d = Math.floor(h / 24);
  return `${d}${isItalian ? 'g fa' : 'd ago'}`;
};

// ---------- Create Ad Modal ----------
const CreateAdDialog = ({ open, onOpenChange, isItalian, onCreated }) => {
  const [kind, setKind] = useState('looking_for_club');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setKind('looking_for_club'); setTitle(''); setDescription(''); };

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      toast.error(isItalian ? 'Inserisci un titolo' : 'Title required');
      return;
    }
    setSaving(true);
    try {
      const res = await marketBoardApi.createAd({ kind, title: t, description: description.trim() });
      toast.success(isItalian ? 'Annuncio pubblicato!' : 'Ad published!');
      onOpenChange(false);
      reset();
      onCreated?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md p-0">
        <DialogHeader className="px-5 py-4 border-b border-white/5">
          <DialogTitle className="font-heading text-base uppercase tracking-wider flex items-center gap-2">
            <Plus size={16} className="text-[#FF00E5]" />
            {isItalian ? 'Nuovo Annuncio' : 'New Ad'}
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {(['looking_for_club', 'looking_for_player']).map((k) => {
              const m = KIND_META[k]; const Icon = m.Icon; const active = kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`flex flex-col items-center gap-1 px-3 py-3 border rounded text-xs uppercase tracking-wider transition-colors ${
                    active ? m.color : 'border-white/10 text-slate-500 hover:text-white'
                  }`}
                  data-testid={`kind-${k}`}
                >
                  <Icon size={16} />
                  <span>{m.label[isItalian ? 'it' : 'en']}</span>
                </button>
              );
            })}
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">
              {isItalian ? 'Titolo' : 'Title'} <span className="text-[#FF1744]">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={80}
              placeholder={isItalian ? 'Es. POR competitivo cerca club serale' : 'e.g. Striker available for evening matches'}
              className="bg-[#050508] border-white/10 text-white"
              data-testid="ad-title-input"
            />
            <p className="text-[10px] text-slate-600 text-right">{title.length}/80</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">
              {isItalian ? 'Descrizione' : 'Description'}
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={5}
              placeholder={isItalian ? 'Disponibilità, ruoli, gamertag, esperienza...' : 'Availability, roles, gamertag, experience...'}
              className="bg-[#050508] border-white/10 text-white resize-none"
              data-testid="ad-description-input"
            />
            <p className="text-[10px] text-slate-600 text-right">{description.length}/1000</p>
          </div>
        </div>
        <DialogFooter className="px-5 py-3 border-t border-white/5 gap-2 sm:gap-2">
          <Button variant="outline" className="border-white/10" onClick={() => onOpenChange(false)} disabled={saving}>
            {isItalian ? 'Annulla' : 'Cancel'}
          </Button>
          <Button className="btn-primary" onClick={submit} disabled={saving} data-testid="ad-submit-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            {isItalian ? 'Pubblica' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Thread Dialog (Messages) ----------
const ThreadDialog = ({ open, onOpenChange, adId, otherUserId, isItalian, onReadAll }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!adId || !otherUserId) return;
    setLoading(true);
    try {
      const res = await marketBoardApi.getThread(adId, otherUserId);
      setData(res.data);
      onReadAll?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [adId, otherUserId, isItalian, onOpenChange, onReadAll]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async () => {
    const t = text.trim(); if (!t) return;
    setSending(true);
    try {
      await marketBoardApi.replyInThread(adId, otherUserId, t);
      setText('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore invio' : 'Send error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-5 py-3 border-b border-white/5 shrink-0">
          <DialogTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2 truncate">
            <MessagesSquare size={14} className="text-[#FF00E5] shrink-0" />
            <span className="truncate">{data?.ad?.title || (isItalian ? 'Conversazione' : 'Conversation')}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" />
              {isItalian ? 'Caricamento…' : 'Loading…'}
            </div>
          )}
          {!loading && data?.messages?.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-8">
              {isItalian ? 'Nessun messaggio.' : 'No messages.'}
            </p>
          )}
          {!loading && data?.messages?.map((m) => {
            const mine = m.from_user_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`market-msg-${m.message_id}`}>
                <div className={`max-w-[80%] px-3 py-1.5 rounded text-sm ${mine ? 'bg-[#FF00E5]/15 border border-[#FF00E5]/30 text-white' : 'bg-[#050508] border border-white/10 text-slate-200'}`}>
                  {!mine && <p className="text-[10px] text-slate-500 mb-0.5">{m.from_gamertag}</p>}
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  <p className="text-[9px] text-slate-600 mt-0.5 text-right">{timeAgo(m.created_at, isItalian)}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-3 border-t border-white/5 shrink-0 flex gap-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            maxLength={2000}
            placeholder={isItalian ? 'Scrivi un messaggio…' : 'Type a message…'}
            className="bg-[#050508] border-white/10 text-white text-sm"
            disabled={sending}
            data-testid="market-message-input"
          />
          <Button size="icon" className="btn-primary shrink-0" onClick={send} disabled={sending || !text.trim()} data-testid="market-message-send">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Contact (First Message) Dialog ----------
const ContactDialog = ({ open, onOpenChange, ad, isItalian, onSent }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { if (!open) setText(''); }, [open]);

  const send = async () => {
    const t = text.trim(); if (!t || !ad) return;
    setSending(true);
    try {
      await marketBoardApi.sendFirstMessage(ad.ad_id, t);
      toast.success(isItalian ? 'Messaggio inviato!' : 'Message sent!');
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md p-0">
        <DialogHeader className="px-5 py-3 border-b border-white/5">
          <DialogTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
            <Send size={14} className="text-[#00F0FF]" />
            {isItalian ? 'Contatta' : 'Contact'} {ad?.author_gamertag}
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 py-4 space-y-3">
          <div className="p-2 bg-[#050508] border border-white/5 rounded text-xs text-slate-400">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{isItalian ? 'Annuncio' : 'Ad'}</p>
            <p className="text-white truncate">{ad?.title}</p>
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder={isItalian ? 'Presentati e spiega perché ti interessa…' : 'Introduce yourself and explain why you’re interested…'}
            className="bg-[#050508] border-white/10 text-white resize-none"
            data-testid="contact-message-input"
          />
          <p className="text-[10px] text-slate-600 text-right">{text.length}/2000</p>
        </div>
        <DialogFooter className="px-5 py-3 border-t border-white/5 gap-2 sm:gap-2">
          <Button variant="outline" className="border-white/10" onClick={() => onOpenChange(false)} disabled={sending}>
            {isItalian ? 'Annulla' : 'Cancel'}
          </Button>
          <Button className="btn-primary" onClick={send} disabled={sending || !text.trim()} data-testid="contact-send-btn">
            {sending && <Loader2 size={14} className="animate-spin mr-1" />}
            {isItalian ? 'Invia' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Ad Card ----------
const AdCard = ({ ad, isItalian, currentUserId, isAdmin, onContact, onDelete }) => {
  const meta = KIND_META[ad.kind];
  const Icon = meta.Icon;
  const isMine = ad.author_user_id === currentUserId;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-[#0a0d15] border rounded-lg p-3 transition-colors ${ad.author_club_premium ? 'border-[#FFD700]/40 hover:border-[#FFD700]/70' : 'border-white/10 hover:border-[#FF00E5]/30'}`}
      data-testid={`market-ad-${ad.ad_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-[10px] uppercase tracking-widest font-bold ${meta.color}`}>
          <Icon size={10} />
          {meta.label[isItalian ? 'it' : 'en']}
        </div>
        <span className="text-[10px] text-slate-600 shrink-0">{timeAgo(ad.created_at, isItalian)}</span>
      </div>
      <h3 className="font-heading text-white text-sm uppercase tracking-wider mb-1 break-words">{ad.title}</h3>
      {ad.description && (
        <p className="text-xs text-slate-400 whitespace-pre-wrap break-words line-clamp-4 mb-2">{ad.description}</p>
      )}
      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-white/5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {ad.author_picture ? (
            <img src={ad.author_picture} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" loading="lazy" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-[10px] text-slate-300 shrink-0">
              {(ad.author_gamertag || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs text-white truncate">{ad.author_gamertag}</p>
            {ad.author_club_name && (
              <p className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                {ad.author_club_premium && <Crown size={10} className="text-[#FFD700] shrink-0" />}
                {ad.author_club_name}
              </p>
            )}
          </div>
        </div>
        {isMine ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete?.(ad)}
            className="text-[#FF1744] hover:bg-[#FF1744]/10 h-7 px-2 text-xs shrink-0"
            data-testid={`ad-delete-${ad.ad_id}`}
          >
            <Trash2 size={12} />
          </Button>
        ) : (
          <div className="flex items-center gap-1 shrink-0">
            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete?.(ad)}
                className="text-[#FF1744] hover:bg-[#FF1744]/10 h-7 px-2 text-xs"
                title={isItalian ? 'Elimina annuncio (admin)' : 'Delete ad (admin)'}
                data-testid={`ad-admin-delete-${ad.ad_id}`}
              >
                <Trash2 size={12} />
              </Button>
            )}
            <Button
              size="sm"
              className="btn-primary h-7 px-3 text-xs"
              onClick={() => onContact?.(ad)}
              data-testid={`ad-contact-${ad.ad_id}`}
            >
              <Send size={11} className="mr-1" />
              {isItalian ? 'Contatta' : 'Contact'}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ---------- Page ----------
const Calciomercato = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isItalian = (user?.language || 'it') === 'it';
  const [tab, setTab] = useState('board'); // 'board' | 'messages'
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(true);
  const [filterKind, setFilterKind] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [contactAd, setContactAd] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [openThread, setOpenThread] = useState(null); // {ad_id, other_user_id}

  const fetchAds = useCallback(async () => {
    setLoadingAds(true);
    try {
      const params = {};
      if (filterKind !== 'all') params.kind = filterKind;
      if (search.trim()) params.q = search.trim();
      const res = await marketBoardApi.listAds(params);
      setAds(res.data?.ads || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore caricamento' : 'Load error'));
    } finally { setLoadingAds(false); }
  }, [filterKind, search, isItalian]);

  const fetchThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const res = await marketBoardApi.myThreads();
      setThreads(res.data?.threads || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally { setLoadingThreads(false); }
  }, [isItalian]);

  useEffect(() => { fetchAds(); }, [fetchAds]);
  useEffect(() => { if (tab === 'messages') fetchThreads(); }, [tab, fetchThreads]);

  const handleDelete = async (ad) => {
    if (!window.confirm(isItalian ? 'Eliminare questo annuncio?' : 'Delete this ad?')) return;
    try {
      await marketBoardApi.deleteAd(ad.ad_id);
      toast.success(isItalian ? 'Annuncio eliminato' : 'Ad deleted');
      fetchAds();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  return (
    <div className="min-h-screen ept-bg-ambient text-white">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4" data-testid="calciomercato-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-300 hover:text-white text-sm" data-testid="market-back-btn">
            <ArrowLeft size={16} />
            {isItalian ? 'Indietro' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={18} className="text-[#FF00E5]" />
            <h1 className="font-heading text-base sm:text-lg uppercase tracking-widest">{isItalian ? 'Calciomercato' : 'Transfer Market'}</h1>
          </div>
          <Button size="sm" className="btn-primary text-xs" onClick={() => setCreateOpen(true)} data-testid="new-ad-btn">
            <Plus size={14} className="mr-1" />
            {isItalian ? 'Annuncio' : 'New Ad'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setTab('board')}
            className={`px-3 py-2 text-xs uppercase tracking-widest font-bold rounded border transition-colors ${
              tab === 'board' ? 'bg-[#FF00E5]/15 border-[#FF00E5]/40 text-[#FF00E5]' : 'bg-[#0a0d15] border-white/10 text-slate-400 hover:text-white'
            }`}
            data-testid="tab-board"
          >
            {isItalian ? 'Bacheca' : 'Board'}
          </button>
          <button
            onClick={() => setTab('messages')}
            className={`px-3 py-2 text-xs uppercase tracking-widest font-bold rounded border transition-colors ${
              tab === 'messages' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/40 text-[#00F0FF]' : 'bg-[#0a0d15] border-white/10 text-slate-400 hover:text-white'
            }`}
            data-testid="tab-messages"
          >
            {isItalian ? 'Messaggi' : 'Messages'}
          </button>
        </div>

        {/* Board */}
        {tab === 'board' && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex gap-1">
                {['all', 'looking_for_club', 'looking_for_player'].map((k) => (
                  <button
                    key={k}
                    onClick={() => setFilterKind(k)}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded border transition-colors ${
                      filterKind === k ? 'bg-[#FF00E5]/15 border-[#FF00E5]/40 text-[#FF00E5]' : 'bg-[#0a0d15] border-white/10 text-slate-400 hover:text-white'
                    }`}
                    data-testid={`filter-${k}`}
                  >
                    {k === 'all'
                      ? (isItalian ? 'Tutti' : 'All')
                      : KIND_META[k].label[isItalian ? 'it' : 'en']}
                  </button>
                ))}
              </div>
              <div className="relative flex-1">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={isItalian ? 'Cerca…' : 'Search…'}
                  className="bg-[#0a0d15] border-white/10 text-white text-sm pl-7 h-8"
                  data-testid="market-search-input"
                />
              </div>
            </div>

            {loadingAds && (
              <div className="flex items-center justify-center gap-2 py-12 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin" />
                {isItalian ? 'Caricamento annunci…' : 'Loading ads…'}
              </div>
            )}

            {!loadingAds && ads.length === 0 && (
              <div className="py-16 text-center text-slate-500 space-y-2" data-testid="board-empty">
                <ArrowLeftRight size={28} className="text-slate-700 mx-auto" />
                <p className="text-sm">{isItalian ? 'Nessun annuncio attivo. Sii il primo a pubblicarne uno!' : 'No active ads. Be the first!'}</p>
              </div>
            )}

            {!loadingAds && ads.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ads.map((ad) => (
                  <AdCard
                    key={ad.ad_id}
                    ad={ad}
                    isItalian={isItalian}
                    currentUserId={user?.user_id}
                    isAdmin={user?.role === 'super_admin' || user?.role === 'admin'}
                    onContact={(a) => setContactAd(a)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {tab === 'messages' && (
          <div className="space-y-2">
            {loadingThreads && (
              <div className="flex items-center justify-center gap-2 py-12 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin" />
                {isItalian ? 'Caricamento…' : 'Loading…'}
              </div>
            )}
            {!loadingThreads && threads.length === 0 && (
              <div className="py-16 text-center text-slate-500 space-y-2" data-testid="threads-empty">
                <MessagesSquare size={28} className="text-slate-700 mx-auto" />
                <p className="text-sm">{isItalian ? 'Nessuna conversazione.' : 'No conversations.'}</p>
              </div>
            )}
            {!loadingThreads && threads.map((th) => {
              const key = `${th.ad_id}-${th.other_user_id}`;
              const cp = th.counterpart || {};
              return (
                <motion.button
                  key={key}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={() => setOpenThread({ ad_id: th.ad_id, other_user_id: th.other_user_id })}
                  className="w-full flex items-center gap-3 px-3 py-2.5 bg-[#0a0d15] border border-white/10 hover:border-[#00F0FF]/30 rounded transition-colors text-left"
                  data-testid={`thread-row-${key}`}
                >
                  {cp.picture ? (
                    <img src={cp.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-xs text-slate-300 shrink-0">
                      {(cp.gamertag || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white font-medium truncate flex-1">{cp.gamertag}</p>
                      <span className="text-[10px] text-slate-600 shrink-0">{timeAgo(th.last_message_at, isItalian)}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">
                      {th.ad_exists ? th.ad_title : (
                        <span className="italic text-slate-600 inline-flex items-center gap-1">
                          <AlertCircle size={10} /> {isItalian ? 'annuncio rimosso' : 'ad removed'}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{th.last_message}</p>
                  </div>
                  {th.unread_count > 0 && (
                    <span className="px-1.5 py-0.5 bg-[#FF00E5] text-white text-[10px] font-bold rounded-full shrink-0">
                      {th.unread_count}
                    </span>
                  )}
                  <ChevronRight size={14} className="text-slate-600 shrink-0" />
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      <CreateAdDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        isItalian={isItalian}
        onCreated={() => fetchAds()}
      />
      <ContactDialog
        open={!!contactAd}
        onOpenChange={(o) => { if (!o) setContactAd(null); }}
        ad={contactAd}
        isItalian={isItalian}
        onSent={() => setTab('messages')}
      />
      <ThreadDialog
        open={!!openThread}
        onOpenChange={(o) => { if (!o) setOpenThread(null); }}
        adId={openThread?.ad_id}
        otherUserId={openThread?.other_user_id}
        isItalian={isItalian}
        onReadAll={() => fetchThreads()}
      />
    </div>
  );
};

export default Calciomercato;
